import React, { useState, useEffect } from "react";
import { CloseOutlined } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Typography,
  IconButton,
  Divider,
  Stack,
  Button,
  TextField,
  InputAdornment,
} from "@mui/material";
import { DropdownOptions } from "../../../components/common/Dropdown/Dropdown";
import { getAccountByClientId } from "../../../services/transactionService";
import "./styles.scss";
import { useClientDetails } from "../../../hooks/useClientDetails";
import { Partner, PartnershipType } from "../../../types/genericType";
import {
  createPartnerUnderPartnership,
  editPartnerUnderPartnership,
  removePartnerUnderPartnership,
} from "../../../services/partnershipService";
import AutoCompleteSelect from "../../../components/common/autocompleteSelect/AutoCompleteSelect";

type NewPartnerPropType = {
  selectedPartnership: PartnershipType;
  setOpen: Function;
  selectedPartner: Partner;
  setOnSuccess: Function;
  setSnackbarMsg: Function;
  getAllPartners: Function;
};

const NewPartner = (props: NewPartnerPropType) => {
  const {
    selectedPartnership,
    setOpen,
    selectedPartner,
    setOnSuccess,
    setSnackbarMsg,
    getAllPartners,
  } = props;
  const { clientOptions } = useClientDetails();
  const [clientId, setClientId] = useState(
    selectedPartner ? selectedPartner?.investor?.id?.toString() : ""
  );
  const [accountId, setAccountId] = useState(
    selectedPartner ? selectedPartner?.account?.id : ""
  );
  const [accountOptions, setAccountOptions] = useState<DropdownOptions[]>([]);
  const [percentOwned, setPercentOwned] = useState(
    selectedPartner ? selectedPartner?.percentage : 0.00
  );
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (clientId) {
      setAccountOptions([]);
      getAccountByClientId(clientId)
        ?.then((res: any) => {
          const accounts: any[] = [];
          res?.forEach((acc: any) => {
            if (acc.isActive) {
              accounts.push({
                value: acc.id,
                label: `${acc.name} - ${acc.accountNumber}`,
              });
            }
          });
          setAccountOptions(accounts);
        })
        .catch((err: any) => {
          console.log(err);
        });
    }
  }, [clientId]);

  useEffect(() => {
    if (selectedPartner?.id && clientOptions.length > 1) {
      setClientId(selectedPartner.investor.id?.toString());
      setPercentOwned(selectedPartner.percentage);
    }
  }, [selectedPartner?.id, clientOptions.length]);

  useEffect(() => {
    if (selectedPartner?.id && accountOptions.length > 1) {
      setAccountId(selectedPartner.account.id.toString());
    }
  }, [selectedPartner?.id, accountOptions.length]);

  const handleAddPartner = () => {
    setIsLoading(true);
    selectedPartnership?.id &&
      createPartnerUnderPartnership(
        selectedPartnership?.id,
        parseInt(accountId.toString()),
        parseFloat(percentOwned.toString())
      )
        .then(() => {
          setIsLoading(false);
          setOnSuccess(true);
          setOpen(false);
          getAllPartners();
          setSnackbarMsg("Partner added successfully");
        })
        .catch(() => {
          setIsLoading(false);
          setOnSuccess(true);
          setSnackbarMsg("Error creating partner");
        });
  };

  const handleEditPartner = () => {
    setIsLoading(true);
    selectedPartnership?.id &&
      editPartnerUnderPartnership(
        selectedPartnership?.id,
        parseInt(selectedPartner?.id.toString()),
        parseInt(accountId.toString()),
        parseFloat(percentOwned.toString())
      )
        .then(() => {
          setIsLoading(false);
          setOnSuccess(true);
          setOpen(false);
          getAllPartners();
          setSnackbarMsg("Partner details updated successfully");
        })
        .catch(() => {
          setIsLoading(false);
          setSnackbarMsg("Error updating partner details");
        });
  };

  const handlePercentageChangev = (event: { target: { value: any } }) => {
    setPercentOwned(event.target.value);
  };

  const handleRemovePartner = () => {
    setIsLoading(true);
    selectedPartnership?.id &&
      removePartnerUnderPartnership(
        selectedPartnership?.id,
        parseInt(accountId.toString())
      )
        .then(() => {
          setIsLoading(false);
          setOnSuccess(true);
          setOpen(false);
          getAllPartners();
          setSnackbarMsg("Partner deleted successfully");
        })
        .catch(() => {
          setIsLoading(false);
          setSnackbarMsg("Error deleting partner");
        });
  };

  return (
    <Box className="modal-container">
      <Box className="header">
        <Typography id="modal-title" variant="h6" component="h2">
          {selectedPartner?.id ? "Edit Partner" : "Add Partner"}
        </Typography>
        <IconButton
          aria-label="toggle password visibility"
          onClick={() => setOpen(false)}
          size="large"
        >
          <CloseOutlined />
        </IconButton>
      </Box>
      <Divider className="mb-16" />
      <Stack spacing={3} className="mb-16">
        <AutoCompleteSelect
          items={clientOptions}
          onSelectItem={setClientId}
          selected={clientId?.toString()}
          placeholder="Select a client"
          label="Client"
          required
          width="315px"
          maxWidthOption="315px"
          disabled={selectedPartner?.id ? true : false}
        />
        <AutoCompleteSelect
          items={accountOptions}
          onSelectItem={setAccountId}
          selected={accountId?.toString()}
          placeholder="Select an account"
          label="Account"
          required
          width="315px"
          maxWidthOption="315px"
        />
        <TextField
          label="Percent Owned"
          className="amount-outlined-input"
          value={percentOwned}
          onChange={handlePercentageChangev}
          placeholder="0.00"
          defaultValue={0.00}
          required
          size="small"
          InputProps={{
            endAdornment: <InputAdornment position="start">%</InputAdornment>,
          }}
        />
      </Stack>
      <Stack direction="row-reverse" spacing={2} className="actions">
        <LoadingButton
          variant="contained"
          data-testid="createTxnBtn"
          size="small"
          className="add-partner-btn"
          onClick={selectedPartner?.id ? handleEditPartner : handleAddPartner}
          loading={isLoading}
          disabled={!clientId || !accountId || !percentOwned}
        >
          {selectedPartner?.id ? "SAVE" : "ADD PARTNER"}
        </LoadingButton>
        {selectedPartner?.id && (
          <LoadingButton
            className="delete-button"
            onClick={handleRemovePartner}
            loading={isLoading}
          >
            REMOVE
          </LoadingButton>
        )}
        <Button className="cancel-button" onClick={() => setOpen(false)}>
          CANCEL
        </Button>
      </Stack>
    </Box>
  );
};

export default NewPartner;
